// src/pages/Reports/IssuanceReporting/ReconciliationDiscrepancy/TicketDiscrepancyTab.tsx
// RPT-010 (README.md mục 7) — chênh lệch phát sinh trong quá trình phát hành/nhập kho/giao/thu
// hồi/đối soát. Có ghi nhận thủ công + xử lý (ngoài các dòng BE tự tạo từ đối soát lệch).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getPaginatedTicketDiscrepanciesApi } from 'helpers/fakebackend_helper';
import { TICKET_DISCREPANCY } from 'helpers/url_helper';
import { buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import AgentSelect from '../shared/AgentSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { DISCREPANCY_TYPE_OPTIONS, INITIAL_REPORT_PAGINATION, ReportPaginationState, TicketDiscrepancy } from '../shared/types';
import CreateDiscrepancyModal from './CreateDiscrepancyModal';
import ResolveDiscrepancyModal from './ResolveDiscrepancyModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const STATUS_OPTIONS = [
  { value: 1, label: st('chua_xu_ly_label') },
  { value: 2, label: st('da_xu_ly_label') },
];

const TicketDiscrepancyTab: React.FC = () => {
  const location = useLocation();
  const [planId, setPlanId] = useState('');
  const [discrepancyType, setDiscrepancyType] = useState('');
  const [agentId, setAgentId] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<TicketDiscrepancy[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap = useMemo(() => buildAgentMap(agents), [agents]);
  const [createOpen, setCreateOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<TicketDiscrepancy | null>(null);

  useEffect(() => { loadAgentsWithCache().then(setAgents); }, []);

  const buildFilterParams = useCallback(
    () => ({
      IssuancePlanId: planId || undefined,
      DiscrepancyType: discrepancyType || undefined,
      AgentId: agentId || undefined,
      Status: status || undefined,
      FromDate: fromDate || undefined,
      ToDate: toDate || undefined,
    }),
    [planId, discrepancyType, agentId, status, fromDate, toDate],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPaginatedTicketDiscrepanciesApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_danh_sach_chenh_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, discrepancyType, agentId, status, fromDate, toDate]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(TICKET_DISCREPANCY, buildFilterParams(), format, 'ChenhLech');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  const canCreate = PageActionPermission.canDisplay(location.pathname, 'button-create-discrepancy');
  const canResolve = PageActionPermission.canDisplay(location.pathname, 'button-resolve-discrepancy');

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={2}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); resetPage(); }} />
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('loai_chenh_lech_label')}</Label>
          <Input type="select" value={discrepancyType} onChange={(e) => { setDiscrepancyType(e.target.value); resetPage(); }}>
            <option value="">{st('tat_ca_option')}</option>
            {DISCREPANCY_TYPE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
            <option value={4}>{st('lech_khi_doi_soat_option')}</option>
          </Input>
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('dai_ly_label')}</Label>
          <AgentSelect value={agentId} onChange={(v) => { setAgentId(v); resetPage(); }} />
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('trang_thai_column')}</Label>
          <Input type="select" value={status} onChange={(e) => { setStatus(e.target.value); resetPage(); }}>
            <option value="">{st('tat_ca_option')}</option>
            {STATUS_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
          </Input>
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('tu_ngay_label')}</Label>
          <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); resetPage(); }} />
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('den_ngay_label')}</Label>
          <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); resetPage(); }} />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="d-flex gap-2 justify-content-end">
          {canCreate && (
            <Button
              color="primary"
              size="sm"
              data-action-code="button-create-discrepancy"
              data-action-name="Ghi nhận chênh lệch"
              disabled={!planId}
              title={!planId ? st('vui_long_chon_ky_phat_hanh_text_3') : undefined}
              onClick={() => setCreateOpen(true)}
            >
              <i className="ri-add-line align-bottom me-1"></i>
              {st('ghi_nhan_chenh_lech_title')}
            </Button>
          )}
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ky_phat_hanh_label')}</th>
                <th>{st('loai_column')}</th>
                <th>{st('dai_ly_label')}</th>
                <th className="text-end">{st('du_kien_column')}</th>
                <th className="text-end">{st('thuc_te_column')}</th>
                <th className="text-end">{st('chenh_lech_column')}</th>
                <th className="text-end">{st('gia_tri_column')}</th>
                <th>{st('ngay_ghi_nhan_column')}</th>
                <th>{st('trang_thai_column')}</th>
                {canResolve && <th style={{ width: 100 }}>{st('thao_tac_column')}</th>}
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={canResolve ? 11 : 10} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.planCode}</td>
                  <td>{row.discrepancyTypeName}</td>
                  <td>{row.agentId != null ? `${getAgentName(agentMap, row.agentId)} (${getAgentCode(agentMap, row.agentId)})` : '-'}</td>
                  <td className="text-end">{formatQty(row.expectedQuantity)}</td>
                  <td className="text-end">{formatQty(row.actualQuantity)}</td>
                  <td className={`text-end ${row.discrepancyQuantity !== 0 ? 'text-danger' : ''}`}>{formatQty(row.discrepancyQuantity)}</td>
                  <td className="text-end">{formatCurrency(row.discrepancyValue)}</td>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td>
                    <span className={`badge bg-${row.status === 2 ? 'success' : 'warning'}-subtle text-${row.status === 2 ? 'success' : 'warning'}`}>
                      {row.statusName}
                    </span>
                  </td>
                  {canResolve && (
                    <td>
                      {row.status === 1 && (
                        <Button
                          color="success"
                          outline
                          size="sm"
                          data-action-code="button-resolve-discrepancy"
                          data-action-name="Xử lý chênh lệch"
                          onClick={() => setResolveTarget(row)}
                        >
                          {st('xu_ly_button')}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}

      <CreateDiscrepancyModal
        isOpen={createOpen}
        planId={Number(planId)}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); loadReport(); }}
      />
      <ResolveDiscrepancyModal
        isOpen={!!resolveTarget}
        target={resolveTarget}
        onClose={() => setResolveTarget(null)}
        onResolved={() => { setResolveTarget(null); loadReport(); }}
      />
    </>
  );
};

export default TicketDiscrepancyTab;
